import * as Sentry from "@sentry/react";

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
// Task #1222 — never initialise Sentry from a test bundle (Vitest sets
// MODE === "test" and VITEST === "true"). The Sentry dashboard was
// dominated by `stripe boom` / `Error: simulated` events from CI.
const isTestEnv =
  import.meta.env.MODE === "test" ||
  // Vitest also sets this in the browser-like environment.
  (import.meta.env as unknown as { VITEST?: unknown }).VITEST === true;

// Frames in `/__tests__/` or files ending with `.test.ts(x)` are test-only
// errors that should never reach the production Sentry dashboard.
function isTestFrame(filename: string | undefined): boolean {
  if (!filename) return false;
  if (/\.test\.[cm]?[jt]sx?$/i.test(filename)) return true;
  if (/\/__tests__\//i.test(filename)) return true;
  return false;
}

const isProd = import.meta.env.MODE === "production";

// Minimal shape of the Sentry event fields `shouldDropSentryEvent` inspects.
// Kept structural (not importing Sentry's full `Event`) so the predicate is a
// pure, easily-testable function with no Sentry runtime dependency.
type SentryEventLike = {
  environment?: string;
  request?: { url?: string };
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
      stacktrace?: { frames?: Array<{ filename?: string; abs_path?: string }> };
    }>;
  };
  // `captureMessage` events carry no `exception` — the text lives on `message`
  // (and Sentry's `logentry`). Inspected so message-level diagnostics (e.g. the
  // MapKit safety-timer warning) can be filtered alongside exception events.
  message?: string;
  logentry?: { message?: string } | null;
  breadcrumbs?: Array<{ data?: { url?: unknown } | null }>;
  // Free-form diagnostic bag Sentry surfaces under "Additional Data". Used by
  // `enrichKeyedPromiseRejection` to attach the real message/stack recovered
  // from a non-Error rejection's `hint.originalException`.
  extra?: Record<string, unknown>;
};

// Task #5064 — third-party hosts whose requests must NOT be turned into
// `http.client` spans by `browserTracingIntegration`. Sentry auto-instruments
// every fetch/resource request, including Apple MapKit's own internal asset
// loads to `cdn.apple-mapkit.com` (`/md/v1/shield`, `/md/v1/icon`, map tiles).
// The natural grid of identical-pattern CDN requests then trips the N+1 API
// Call detector (Sentry DRIVESHARE_V2-8G / -8E) even though these are
// third-party loads we neither own nor can reduce. Excluding them at
// span-creation time means the detector has nothing to group. N+1 issues are
// `transaction`-type events, so `shouldDropSentryEvent` (an error `beforeSend`
// predicate) never touches them — this MUST live at span creation.
const THIRD_PARTY_SPAN_DENY_HOSTS: RegExp[] = [
  /(^|\.)apple-mapkit\.com$/i, // Apple MapKit CDN — shields, icons, tiles
  /(^|\.)stripe\.com$/i, // Stripe.js + API
  /(^|\.)stripe\.network$/i, // Stripe 3DS / fraud frames
  /(^|\.)posthog\.com$/i, // PostHog ingestion (if ever hit directly)
];

// Same-origin paths that merely proxy to a third party. These are first-party
// URLs (so the host check above misses them) but the work happens off-platform,
// so we don't want N+1 spans for them either (e.g. the PostHog reverse proxy).
const FIRST_PARTY_PROXY_DENY_PATHS: RegExp[] = [/\/_a\/ph\//i];

/**
 * Pure predicate for `browserTracingIntegration({ shouldCreateSpanForRequest })`:
 * returns `false` for requests to third-party hosts (or same-origin proxy paths)
 * we don't own, so no `http.client` span is created and the N+1 detector has
 * nothing to group. Returns `true` for everything else (our own `/api/...`
 * calls, navigations, etc.). Exported so the exclusion is unit-tested and
 * protected against refactor regressions.
 */
export function shouldCreateSpanForRequest(url: string): boolean {
  try {
    const base =
      typeof window !== "undefined" && window.location
        ? window.location.origin
        : "http://localhost";
    const parsed = new URL(url, base);
    if (THIRD_PARTY_SPAN_DENY_HOSTS.some((re) => re.test(parsed.hostname))) {
      return false;
    }
    if (FIRST_PARTY_PROXY_DENY_PATHS.some((re) => re.test(parsed.pathname))) {
      return false;
    }
    return true;
  } catch {
    // Unparseable URL — default to the safe (instrumented) behavior.
    return true;
  }
}

// Minimal shape of a Sentry span inside a transaction event. Kept structural
// (not importing Sentry's `SpanJSON`) so the span-filter predicates stay pure
// and unit-testable with no Sentry runtime dependency.
type SpanLike = {
  op?: string;
  description?: string;
  data?: Record<string, unknown> | null;
};

type TransactionEventLike = {
  spans?: SpanLike[];
};

/**
 * Returns `true` when a span reference (a full/relative URL, or a bare hostname
 * like `server.address`) points at a third-party host we don't own. Reuses
 * `shouldCreateSpanForRequest` for anything URL-shaped so the URL and the
 * transaction-span paths share one source of truth.
 */
function isDeniedSpanReference(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  // Full (`https://…`) or relative (`/api/…`) URL → reuse the span-creation
  // predicate (inverted): denied when it would not have created a span.
  if (/:\/\//.test(v) || v.startsWith("/")) {
    return !shouldCreateSpanForRequest(v);
  }
  // Bare hostname (e.g. a span's `server.address` = "cdn.apple-mapkit.com").
  return THIRD_PARTY_SPAN_DENY_HOSTS.some((re) => re.test(v));
}

/**
 * Pure predicate: returns `true` when a transaction span targets one of the
 * third-party deny hosts (Apple MapKit, Stripe, PostHog proxy) and should be
 * stripped from the transaction before it is sent.
 *
 * Task #5240 — `shouldCreateSpanForRequest` (Task #5064) only governs the
 * fetch/XHR `http.client` span instrumentation. Apple MapKit's map-tile loads
 * (`cdn.apple-mapkit.com/md/v1/vtile`) ALSO surface as resource-timing spans
 * that `browserTracingIntegration` backfills onto the navigation transaction —
 * a path `shouldCreateSpanForRequest` never sees. Those leftover tile spans are
 * what Sentry's server-side performance detectors group into the "Large HTTP
 * payload" (DRIVESHARE_V2-8W) and "N+1 API Calls" (DRIVESHARE_V2-93) issues on
 * `/uk/search`, even though they are third-party loads we neither own nor can
 * reduce. Stripping them here means the detectors have nothing to group.
 */
export function shouldDropTransactionSpan(span: SpanLike): boolean {
  const candidates: string[] = [];
  // Descriptions look like "GET https://host/path" — test each whitespace token
  // so the URL is matched regardless of the leading HTTP verb.
  if (typeof span.description === "string") {
    for (const tok of span.description.split(/\s+/)) candidates.push(tok);
  }
  const data = span.data || {};
  for (const key of ["http.url", "url", "server.address"]) {
    const v = data[key];
    if (typeof v === "string") candidates.push(v);
  }
  return candidates.some(isDeniedSpanReference);
}

/**
 * Removes third-party deny-host spans from a transaction event's `spans` array
 * (see `shouldDropTransactionSpan`). The dropped spans are always leaf HTTP /
 * resource spans, so removing them never orphans a child. Returns the (mutated)
 * event so it can be used directly as `beforeSendTransaction`. Exported so the
 * filtering is unit-tested and protected against refactor regressions.
 */
export function filterTransactionSpans<T extends TransactionEventLike>(event: T): T {
  if (Array.isArray(event.spans) && event.spans.length > 0) {
    event.spans = event.spans.filter((s) => !shouldDropTransactionSpan(s));
  }
  return event;
}

/**
 * Pure `beforeSend` filtering predicate: returns `true` when the event is noise
 * that should be dropped (the caller returns `null`), `false` when it should be
 * reported. Extracted from `Sentry.init({ beforeSend })` (Task #4696) so the
 * many suppressors — including the dev-only HMR-transient drops added in Task
 * #4690 — can be unit-tested and protected from accidental refactor regressions.
 *
 * `prod` gates the dev-only suppressors so a genuine production occurrence of
 * those shapes still reports.
 */
export function shouldDropSentryEvent(
  event: SentryEventLike,
  prod: boolean,
): boolean {
  // Task #1222 — drop test errors that name a `*.test.ts` frame even
  // if they somehow leak into a non-test bundle (e.g. dev preview
  // running unit tests in another tab).
  const topFrames = event.exception?.values?.flatMap((v) => v.stacktrace?.frames || []) || [];
  if (topFrames.some((f) => isTestFrame(f.filename || f.abs_path))) return true;

  // Third-party Apple MapKit internal errors (e.g. Sentry event
  // a9fff3a39f0e44bb8f6b05fa0c5f408f: `TypeError: … reading 'Vb'` thrown
  // inside `cdn.apple-mapkit.com/ti/csr/…/mk-csr.js` canvas listeners after a
  // long-idle tab resumes and MapKit's WebGL renderer loses its context).
  // These are unhandled only because Sentry's addEventListener wrapper
  // captures them; our code isn't on the stack and the map self-recovers on
  // MapKit's next bootstrap. Kept tight: EVERY frame must be either the
  // Sentry wrapper helper or an apple-mapkit.com frame — if any first-party
  // frame is present (our callbacks into MapKit APIs), the event still
  // reports.
  if (topFrames.length > 0) {
    const isMapKitFrame = (f: { filename?: string; abs_path?: string }) =>
      /apple-mapkit\.com\//i.test(f.abs_path || f.filename || "");
    const isSentryHelperFrame = (f: { filename?: string; abs_path?: string }) =>
      /@sentry[+/]browser/i.test(f.abs_path || f.filename || "");
    if (
      topFrames.some(isMapKitFrame) &&
      topFrames.every((f) => isMapKitFrame(f) || isSentryHelperFrame(f))
    ) {
      return true;
    }
  }

  // Production error evidence on 2026-08-28 showed the only remaining
  // Array.prototype.at failures originate inside Cloudflare Browser Insights
  // or web-vitals attribution on legacy clients. Our supported source has no
  // production `.at()` call sites. Keep this filter deliberately narrow:
  // exact observed messages, at least one known third-party frame, and EVERY
  // frame must belong to that dependency/vendor (or Sentry's wrapper). A
  // first-party frame therefore keeps reporting.
  const thirdPartyArrayAtFailure = event.exception?.values?.some((value) =>
    value.type === "TypeError"
    && (
      value.value === "t.entries.at is not a function"
      || value.value === "this.i.at is not a function"
    ),
  );
  if (thirdPartyArrayAtFailure && topFrames.length > 0) {
    const isKnownThirdPartyFrame = (frame: { filename?: string; abs_path?: string }) => {
      const path = frame.abs_path || frame.filename || "";
      return /static\.cloudflareinsights\.com\/beacon\.min\.js/i.test(path)
        || /node_modules\/(?:\.pnpm\/)?web-vitals(?:@|\/)/i.test(path)
        || /@sentry[+/]browser/i.test(path);
    };
    if (topFrames.some(isKnownThirdPartyFrame) && topFrames.every(isKnownThirdPartyFrame)) {
      return true;
    }
  }

  // Apple MapKit's obscured bootstrap occasionally calls assignedNodes on a
  // non-slot element while laying out annotations. The observed groups contain
  // the exact TypeError and MapKit's synthetic <obscura:bootstrap> frames; no
  // customer identity or first-party business operation is involved. Keep the
  // drop narrow so any similarly-worded first-party error still reports.
  const mapKitAssignedNodesFailure = event.exception?.values?.some((value) =>
    value.type === "TypeError" && value.value === "e.assignedNodes is not a function"
  );
  if (mapKitAssignedNodesFailure && topFrames.length > 0) {
    const isObservedMapKitFrame = (frame: { filename?: string; abs_path?: string }) => {
      const path = frame.abs_path || frame.filename || "";
      return path === "<obscura:bootstrap>"
        || path === "<anonymous>"
        || /@sentry[+/]browser/i.test(path)
        || /src\/components\/maps\/AppleMap\.tsx$/i.test(path);
    };
    if (
      topFrames.some((frame) => (frame.abs_path || frame.filename) === "<obscura:bootstrap>")
      && topFrames.every(isObservedMapKitFrame)
    ) {
      return true;
    }
  }

  // Task #4690 — dev/HMR transient context errors (Sentry
  // DRIVESHARE_V2-7Y, -7X, -7T, -3Q, -7V), all captured during a single
  // dev session on localhost / *.janeway.replit.dev with zero production
  // occurrences. Two shapes:
  //   1. `useUser must be used within an AuthProvider` — when Vite hot-
  //      reloads a subtree, a child (CarCard→useFavourite, HostMfaNudge,
  //      LanguageRegionModal, useFeatureFlags, …) can render for one tick
  //      before the AuthProvider re-establishes React context, so the
  //      context reads `null` and the hook throws.
  //   2. `ReferenceError: <symbol> is not defined` — a stale HMR chunk
  //      references a symbol at an out-of-date line (e.g. the reported
  //      `DriveShareLoginNudgeWatcher` is in fact imported + rendered in
  //      the current code, yet errors at a stale `AuthModal.tsx:130`).
  // Both are dev-only hot-reload artifacts, not real bugs, so drop them
  // outside production. Kept tight to the exact shapes (and gated to
  // non-production) so a genuine production occurrence still reports.
  if (!prod) {
    const hmrTransient = event.exception?.values?.some((v) => {
      const val = v.value || "";
      const typ = v.type || "";
      if (val === "useUser must be used within an AuthProvider") return true;
      if (typ === "ReferenceError" && / is not defined$/.test(val)) return true;
      return false;
    });
    if (hmrTransient) return true;

    // Admin reports intentionally probe several privileged endpoints while a
    // development session is bootstrapping. A non-admin or AAL1 local session
    // receives this expected denial; it is not evidence of a production
    // authorization defect. Keep the filter tied to the exact error and a
    // local/development event so real permission regressions still report.
    const localAdminPermissionProbe = event.exception?.values?.some((v) =>
      v.type === "AdminApiError" && v.value === "Insufficient permissions",
    );
    const eventUrl = event.request?.url || "";
    if (
      localAdminPermissionProbe
      && (event.environment === "development" || /^https?:\/\/localhost(?:[:/]|$)/i.test(eventUrl))
    ) {
      return true;
    }
  }

  // Task #5334 — `mapkit:safety-timer-no-region-change-end` (captured via
  // `captureMessage`, level "warning", tags.feature=mapkit; Sentry
  // DRIVESHARE_V2). This is the blank-map safety-timer diagnostic. Its trigger
  // — `region-change-end` not firing within ~8s of map construction — is a
  // "camera moved" signal, NOT "tiles painted": a single-pin static map that
  // frames itself to its own construction region legitimately never moves the
  // camera, so the event never fires even though tiles painted fine. The
  // emission site already gates on a no-paint tile-grid check, but that probe
  // reads cross-origin resource timing which can't always see tile sizes, so a
  // residual single-pin false positive still slips through. It is a warning-
  // level diagnostic the UI degrades around (no user-facing break), so drop it
  // here as the belt-and-braces filter. Kept tight to the exact message string
  // so the error-level MapKit captures (token/script/auth failures, which carry
  // distinct messages at level "error") are unaffected.
  const messageText =
    (typeof event.message === "string" ? event.message : "") ||
    (event.logentry?.message ?? "");
  if (messageText === "mapkit:safety-timer-no-region-change-end") return true;

  const dominated = event.exception?.values?.some((v) => {
    const val = v.value || "";
    const typ = v.type || "";
    if (typ === "NotFoundError" || val.includes("404")) return true;
    // Task #6908 — `session_expired` (Sentry DRIVESHARE_V2-8F, -C2). An
    // expired login is an expected auth state, not a defect: the dashboard
    // and API clients already handle it by redirecting to
    // /login?reason=session_expired. Kept tight to the exact value so a
    // genuine error that merely MENTIONS session expiry still reports.
    if (val === "session_expired") return true;
    if (/API error (502|503|504)/.test(val)) return true;
    if (val.includes("Missing required environment variable")) return true;
    if (/Index .* does not exist/.test(val) || (val.includes("ApiError") && val.includes("does not exist"))) return true;
    if (val.includes("dynamically imported module") || val.includes("Failed to fetch dynamically imported module")) return true;
    if (typ === "TypeError" && (val === "Failed to fetch" || val === "Load failed")) return true;
    // Task #5241 — `TypeError: Load failed (driveshare-assets.com)` (Sentry
    // DRIVESHARE_V2-8P, iOS Safari / in-app browsers). Same transient
    // resource-load failure as the bare `Load failed` drop above, but Sentry
    // appends the failing resource host in parentheses. It's a network/CDN
    // fetch that never completed (flaky mobile connection / asset host blip),
    // not an app bug, and the call sites already degrade gracefully (the image
    // / asset simply doesn't render). Kept tight to the `Load failed (host)`
    // and `Failed to fetch (host)` shapes so a genuine TypeError still reports.
    if (typ === "TypeError" && (/^Load failed \(.+\)$/.test(val) || /^Failed to fetch \(.+\)$/.test(val))) return true;
    if (/Loading chunk .* failed/i.test(val)) return true;
    // Task #5334 — terminal network `ChunkLoadError` from the hardened lazy
    // loader (`lib/lazyRetry.ts`). The loader already RETRIES the import
    // in-memory, re-imports the chunk under a cache-busted URL, and performs a
    // one-time cache-busted full-page reload to pick up the fresh bundle after a
    // deploy; it only throws "...failed to load after reload" once the chunk
    // STILL could not be FETCHED after all of that. In practice that is a
    // stale/poisoned CDN chunk straddling a deploy (environmental), not a
    // first-party bug, and the user's next clean load recovers. So stop the
    // dashboard from counting these auto-recovered fetch failures as crashes.
    // IMPORTANT: matched tight to the network terminal message only — the
    // loader's OTHER terminal `ChunkLoadError` ("...resolved without a usable
    // default/named export") means the chunk DID download but its module shape
    // is wrong even after a reload, which is a genuine build/export regression
    // that MUST stay visible, so it is deliberately NOT dropped here.
    if (typ === "ChunkLoadError" && /failed to load after reload/i.test(val)) return true;
    // Task #1222 — PostHog first-party proxy CORS storm
    // (Sentry DRIVESHARE_V2-36, ~885 events). Match by message
    // and (defensively) by the `_a/ph` URL appearing in any
    // breadcrumb so unrelated CORS errors still report.
    if (val.includes("Not allowed by CORS")) {
      const breadcrumbs = event.breadcrumbs || [];
      const phMatch = breadcrumbs.some((b) => {
        const url = (b.data && (b.data.url as string | undefined)) || "";
        return typeof url === "string" && url.includes("/_a/ph/");
      });
      if (phMatch || breadcrumbs.length === 0) return true;
    }
    // Task #1222 — expected `ApiError: Authentication required`
    // from unauthenticated users-api fetches on the homepage
    // (DRIVESHARE_V2-32). The call sites have been gated on
    // session, so this is belt-and-braces.
    if (typ === "ApiError" && /Authentication required/i.test(val)) return true;
    if (val === "Authentication required" || /Authentication required/i.test(val) && val.length < 80) {
      // Be conservative: only suppress when it's the bare auth
      // string (no extra context that suggests a real bug).
      if (typ === "ApiError" || typ === "Error") return true;
    }
    // Task #8042 — bot 403 noise on the search surface (Sentry issue
    // V2-C7, 108 events, 0 real users). Crawler/bot sessions hit
    // /search and the WAF/bot-protection 403s the search API fetches
    // (`/api/cars/search`, `/api/listings/lookup`); the client then captures
    // `ApiError: API error 403`. Real users are never bot-403'd (0 users in
    // 24h triage) and the page already degrades gracefully. Kept tight: the
    // EXACT bare `API error 403` message AND a breadcrumb showing the 403 came
    // from a search API endpoint — a 403 from any other endpoint (e.g. a real
    // permissions bug on bookings) still reports.
    if (typ === "ApiError" && val === "API error 403") {
      // Correlate the 403 to its actual request: consider only http
      // breadcrumbs that RECORDED a 403 status. Drop only when at least one
      // exists and EVERY 403 breadcrumb is a search-API endpoint — a session
      // that searched earlier and then got a genuine 403 from another
      // endpoint (bookings, checkout, …) has a non-search 403 breadcrumb and
      // still reports.
      const isSearchApiUrl = (url: string) =>
        url.includes("/api/cars/search") || url.includes("/api/listings/lookup");
      const crumbs403 = (event.breadcrumbs || []).filter((b) => {
        const data = (b.data || {}) as { url?: unknown; status_code?: unknown };
        return data.status_code === 403 && typeof data.url === "string";
      });
      if (
        crumbs403.length > 0 &&
        crumbs403.every((b) => isSearchApiUrl((b.data as { url: string }).url))
      ) {
        return true;
      }
    }
    // Task #1484 — `ApiError`/`BookingApiError: Invalid token`
    // from `middleware/auth.ts` when a user's Supabase session
    // expires mid-session (every ~hour). The dashboard fires
    // ~15 parallel queries that all 401, spamming Sentry with
    // identical alerts (DRIVESHARE_V2 alert May 2026). The
    // user is silently re-authed by AuthProvider on the next
    // tick, so these are operational noise, not bugs. Suppress
    // for both ApiError + BookingApiError + bare Error variants.
    if ((typ === "ApiError" || typ === "BookingApiError" || typ === "Error") && val === "Invalid token") return true;
    // Task #6908 — `session_expired` (Sentry DRIVESHARE_V2-8F,
    // DRIVESHARE_V2-C2). Thrown by `authFetch` in bookings-api /
    // subscriptions-api when the server 401s with
    // `{ error: "session_expired" }` (middleware/auth.ts — password
    // rotated or absolute session max age) and the silent token refresh
    // also fails. This is an expected auth-lifecycle state, not a bug:
    // the dashboard's `sessionExpired` flag redirects to
    // /login?reason=session_expired. Suppress the exact bare slug for
    // the same trio of error types as the `Invalid token` drop above so
    // any richer/unexpected message still reports.
    if ((typ === "ApiError" || typ === "BookingApiError" || typ === "Error") && val === "session_expired") return true;
    // Task #5423 — `ApiError: User profile not found` (Sentry
    // DRIVESHARE_V2-2P) is thrown by `authFetch` (users-api.ts) when a user
    // holds a valid Supabase session but has no `public.users` row yet —
    // commonly legacy-migrated / no-email accounts that haven't completed
    // profile creation. This is a known "authenticated-but-no-profile" state,
    // NOT a bug: the server's /auth/ensure-user returns
    // account_status:"needs_profile" (200, not 404) so AuthProvider funnels
    // these users into the profile-completion dialog, and every page/global
    // consumer of the authed endpoints already degrades to a neutral empty
    // state (counts → 0, settings/host-status swallowed). The 173-event storm
    // came from car-detail and other authed surfaces re-issuing /users/me/*
    // calls that 404 until the profile exists. Suppress centrally — mirroring
    // the `Authentication required` / `Invalid token` handled-auth drops above
    // — so it never floods the dashboard regardless of which caller throws.
    // Kept tight to the exact ApiError/Error shape + message so a genuinely
    // unexpected "not found" still reports.
    if ((typ === "ApiError" || typ === "Error") && val === "User profile not found") return true;
    // The quote and booking clients deliberately convert their bounded
    // watchdog aborts into typed errors so the UI can leave its loading state
    // and offer a retry. The watchdog has already emitted a bounded warning
    // with route/classification context; dropping the propagated exception
    // avoids creating a duplicate error issue for the same expected timeout.
    // Keep this exact to the typed names/codes so unrelated timeout failures
    // and payment errors remain reportable.
    if (
      (typ === "QuoteTimeoutError" && val === "The price request is taking longer than expected. Please try again.") ||
      (typ === "BookingApiTimeoutError" && val === "The booking service is taking longer than expected. Please try again.")
    ) return true;
    // Task #3595 — `RateLimitError: Too many attempts` (Sentry
    // DRIVESHARE_V2-5G) is thrown by `authFetch` when a users-api
    // endpoint returns HTTP 429. It is a handled, expected condition:
    // the client already parses `Retry-After`/`retryAfter` and the call
    // sites surface a friendly "please wait ~N seconds" message, so it
    // is operational noise (a hammered user), not a bug. Suppress so it
    // never reaches the dashboard as an error-level event.
    if (typ === "RateLimitError" || /Too many attempts/i.test(val)) return true;
    // Task #5897 — `ApiError: account_suspended` (Sentry DRIVESHARE_V2-4X) is
    // thrown by `authFetch` when a users-api endpoint returns the intended
    // HTTP 403 for a suspended account (`rejectSuspendedUser` on the server).
    // It is a deliberate, expected condition — the account IS suspended — not a
    // bug: the client surfaces the suspension to the user gracefully (Auth
    // provider signs the suspended session out with a clear message). So it is
    // operational noise, not an error. Suppress centrally — mirroring the
    // `RateLimitError` / missing-profile drops above — so it never floods the
    // dashboard regardless of which authed caller throws it. Kept tight to the
    // exact ApiError/Error shape + message so an unexpected value still reports.
    if ((typ === "ApiError" || typ === "Error") && val === "account_suspended") return true;
    // Task #4249 — defense in depth for `BookingApiError`/`ApiError:
    // API error 429` (Sentry DRIVESHARE_V2-7E). bookings-api now
    // throws the suppressed `RateLimitError` on a 429, but some code
    // path could still throw a generic 429 (e.g. an empty 429 body
    // falling through to the bare "API error 429" message). A handled
    // 429 is operational noise (the dashboard's parallel load burst
    // tripping the per-IP limiter), so drop it too. Kept narrow to the
    // handled rate-limit shape so genuine errors still report.
    // `QuoteApiError` is included because the quote path (`apiFetch` in
    // bookings-api) throws `QuoteApiError: API error 429` on an empty 429 body
    // — it does NOT go through `authFetch`'s `RateLimitError` mapping — so a
    // hammered quote (e.g. repeated same-date chauffeured re-quotes tripping the
    // per-IP limiter) surfaced as an error-level event (DRIVESHARE_V2-8Q).
    if (
      (typ === "BookingApiError" ||
        typ === "ApiError" ||
        typ === "QuoteApiError" ||
        typ === "Error") &&
      /API error 429/.test(val)
    )
      return true;
    // Synthetic test errors — see verification-hold-lifecycle.test.ts
    // and pending-liability-warnings.test.ts.
    if (val === "stripe boom" || val.startsWith("simulated")) return true;
    if (typ === "AbortError" && /lock was stolen/i.test(val)) return true;
    if (/was released because another request stole it/i.test(val)) return true;
    // Task #5724 — generic `AbortError: signal is aborted without reason`
    // (Sentry driveshare_v2). This is the DOMException a browser raises when an
    // in-flight `fetch` is cancelled via `AbortController.abort()` with no
    // explicit reason (the default reason string is "signal is aborted without
    // reason"; some engines report the shorter "aborted without reason"). Every
    // AbortController call site in the app aborts a request the user has moved
    // on from — checkout tax recalc, booking/quote refetch, search + address
    // lookup, and the auth ensure-user timeout — and each already swallows its
    // own abort (the promise's `.catch`/try-catch degrades gracefully). So a
    // generic abort is an expected transient, not a bug. Kept scoped to the
    // `AbortError` type AND the "aborted without reason" message so a genuinely
    // unexpected AbortError with a real reason still reports (this also stays
    // disjoint from the specific "lock was stolen" web-locks drop above).
    if (typ === "AbortError" && /aborted without reason/i.test(val)) return true;
    // Task #5724 — `TypeError: null is not an object (evaluating
    // 'parent.document')` (Sentry driveshare_v2). Benign third-party /
    // browser-extension noise, not an app bug: nothing in our source reads
    // `parent.document` (we never reach into a parent frame's document), so
    // this can only originate from an injected script / content blocker running
    // inside an iframe context whose `parent` is cross-origin (or null). Kept
    // tight to the `TypeError` type AND an explicit `parent.document` mention so
    // a genuine first-party TypeError is unaffected.
    if (typ === "TypeError" && val.includes("parent.document")) return true;
    // Task #5724 — `SyntaxError: Unexpected token 'else'` (Sentry
    // driveshare_v2). Injected-script noise, not an app bug: our production
    // bundle is minified, so it can never emit a readable `else`-token syntax
    // error — a parse error in our own code would surface as a build failure,
    // not a runtime SyntaxError with human-readable source. This shape is the
    // fingerprint of a browser extension / injected `<script>` whose own source
    // fails to parse in the page context. Kept tight to the exact
    // `Unexpected token 'else'` shape so a genuine SyntaxError still reports.
    if (typ === "SyntaxError" && /Unexpected token 'else'/.test(val)) return true;
    // Task #4391 — `SecurityError: Blocked a frame with origin "…" from
    // accessing a cross-origin frame` (Sentry DRIVESHARE_V2-7M). This is
    // benign third-party noise, not an app bug: the search map is drawn
    // directly into the DOM via Apple MapKit, so there are no first-party
    // cross-origin iframes our code reaches into. The signature (minified
    // `c` attributed to the document, single occurrence on a Safari beta)
    // is the classic fingerprint of a browser extension / content blocker
    // or a Safari beta feature injecting a cross-origin frame. Kept tight
    // to this exact shape (DOMException code 18) so genuine errors report.
    if (typ === "SecurityError" && /Blocked a frame with origin .* from accessing a cross-origin frame/.test(val)) return true;
    // Task #5334 — storage-access `SecurityError` (Sentry DRIVESHARE_V2).
    // "Block All Cookies" / partitioned-storage WebViews throw a `SecurityError`
    // the instant first-party code touches `localStorage` / `sessionStorage`
    // (e.g. Chromium "Failed to read the 'localStorage' property from 'Window':
    // Access is denied for this document."). Every first-party storage access
    // already runs inside try/catch and degrades gracefully (the value simply
    // isn't persisted), so this is an environment limitation, not an app bug.
    // Kept tight to the SecurityError type AND an EXPLICIT localStorage/
    // sessionStorage mention in the message — generic SecurityError phrases
    // (bare "The operation is insecure." / "Access is denied for this document")
    // are not storage-unique, so they are deliberately NOT matched and still
    // report (this also keeps it disjoint from the cross-origin-frame drop above).
    if (typ === "SecurityError" && /(localStorage|sessionStorage)/i.test(val)) return true;
    // Task #5102 — `TypeError: null is not an object (evaluating
    // 'this._mapNode.rotation')` (Sentry driveshare_v2, route `/uk/search`).
    // Benign third-party noise, not an app bug: the entire stack trace is inside
    // Apple's own `mapkit.js` bundle (`rotation` → `_usingCSRAPI` →
    // `_logFallback` → `handleFallback`) with zero first-party frames. MapKit
    // reads `this._mapNode.rotation` during its internal WebGL→CSR renderer-
    // fallback telemetry; when the map's DOM node has already been nulled (map
    // destroyed/unmounted, or the fallback fires against a not-yet-attached
    // node) the read throws. The app already degrades gracefully (the map UI
    // falls back / disappears with no user-facing break) and the crash is inside
    // Apple's minified bundle, so suppression mirrors the MapKit SecurityError
    // drop above. Kept tight: requires the exact `_mapNode.rotation` signature
    // AND that every stack frame is within `mapkit.js`, so a genuine first-party
    // TypeError that merely mentions `_mapNode.rotation` still reports.
    if (typ === "TypeError" && val.includes("this._mapNode.rotation")) {
      const frames = v.stacktrace?.frames || [];
      const inMapKit = (f: { filename?: string; abs_path?: string }) =>
        /mapkit\.js/i.test(f.filename || f.abs_path || "");
      if (frames.length > 0 && frames.every(inMapKit)) return true;
    }
    // Task #5241 — `TypeError: Cannot read properties of undefined (reading
    // 'maxWidth')` (Sentry DRIVESHARE_V2-96, route /it/cars/...). Benign
    // third-party noise, not an app bug: the whole stack is inside Apple
    // MapKit's WebGL renderer (`mapkit.js` → `mk-csr.js` `Object.resize`),
    // reached from a requestAnimationFrame callback (mechanism
    // auto.browser.browserapierrors.requestAnimationFrame). MapKit reads
    // `d.maxWidth`/`d.maxHeight` off an internal renderer cache entry that is
    // `undefined` when the map canvas is resized after the WebGL context was
    // lost / the map node detached. The map degrades gracefully (it just stops
    // painting) and no first-party frame touches `maxWidth`. Mirrors the
    // `_mapNode.rotation` drop above: requires the exact `maxWidth` signature
    // AND that every frame is Apple's MapKit bundle or Sentry's own
    // instrumentation wrapper (the rAF handler is wrapped by `sentryWrapped`,
    // so a Sentry node_modules frame can sit on top of the MapKit stack). A
    // genuine first-party `/src/` TypeError that merely mentions `maxWidth`
    // still reports because that frame is neither MapKit nor Sentry-internal.
    if (typ === "TypeError" && val.includes("maxWidth")) {
      const frames = v.stacktrace?.frames || [];
      const isMapKitFrame = (f: { filename?: string; abs_path?: string }) =>
        /(mapkit|mk-csr)\.js/i.test(f.filename || f.abs_path || "");
      const isSentryInternalFrame = (f: { filename?: string; abs_path?: string }) =>
        /@sentry|sentry\+browser/i.test(f.filename || f.abs_path || "");
      const hasMapKitFrame = frames.some(isMapKitFrame);
      if (
        hasMapKitFrame &&
        frames.every((f) => isMapKitFrame(f) || isSentryInternalFrame(f))
      )
        return true;
    }
    // Task #5241 — `Error: [insights middleware]: could not load
    // search-insights.js` (Sentry DRIVESHARE_V2-98, route /no/cars/...).
    // Third-party / ad-blocker noise, not an app bug: Algolia InstantSearch's
    // insights middleware appends Algolia's `search-insights.js` from their CDN
    // and emits this Error from the script's `onerror` when the load fails —
    // almost always a tracker/ad blocker or flaky mobile network blocking the
    // third-party CDN. Click-analytics degrade silently (search itself still
    // works), so it is environment noise. Kept tight to the exact middleware
    // message so an unrelated error still reports.
    if (/could not load search-insights\.js/i.test(val)) return true;
    // Task #5241 — content-less unhandled-rejection noise:
    //   • `Object captured as promise rejection with keys: [object has no
    //     keys]` (Sentry DRIVESHARE_V2-9F) — Sentry synthesises this for a
    //     non-Error promise rejection; `[object has no keys]` means it was an
    //     empty `{}` with no stack and nothing actionable.
    //   • `CustomEvent: Event \`CustomEvent\` (type=unhandledrejection)
    //     captured as promise rejection` (DRIVESHARE_V2-9G, isTrusted:false) —
    //     a forged `unhandledrejection` CustomEvent dispatched by a browser
    //     extension / injected script, not the page itself.
    // Neither carries a first-party frame or payload to act on. Kept tight to
    // the empty-keys shape (a non-empty object rejection lists its keys and
    // still reports) and to the synthetic CustomEvent unhandledrejection shape.
    if (/captured as promise rejection with keys: \[object has no keys\]/i.test(val)) return true;
    if (typ === "CustomEvent" && /type=unhandledrejection/i.test(val)) return true;
    // Task #5001 — `Error invoking enableDidUserTypeOnKeyboardLogging: Java
    // object is gone` (browser `Facebook 565.0.0`, route `/uk/...`). This is
    // benign third-party noise, not an app bug: `enableDidUserTypeOnKeyboardLogging`
    // is a JS bridge method injected by the Facebook in-app browser (Android
    // WebView), and `Java object is gone` is the Android WebView error thrown
    // when that injected script calls its native bridge after the WebView's
    // Java object has been garbage-collected (user navigated away / backgrounded
    // the in-app browser). No such function exists anywhere in our code.
    // Applied unconditionally (pure third-party injection noise). Kept tight to
    // the Facebook in-app browser fingerprint so genuine errors still report.
    if (val.includes("enableDidUserTypeOnKeyboardLogging") && val.includes("Java object is gone")) return true;
    // DRIVESHARE_V2-6R — `Error: Forward geocode failed: 5xx` thrown by the
    // search pages' MapKit forward-geocode proxy when the upstream gateway
    // returns a transient 502/503/504. The call sites already catch this
    // and fall back gracefully (no map pin), so it is transient upstream
    // noise, not an app bug — mirrors the `API error 502/503/504` drop
    // above. Kept to gateway 5xx so a genuine geocode 500 still reports.
    if (/Forward geocode failed: (502|503|504)/.test(val)) return true;
    // DRIVESHARE_V2-60 — `Error: Feature not configured` is the typed 503
    // soft-fail from api-server `admin-soft-fail.ts` when an admin table /
    // column isn't provisioned in this environment (e.g. site_content). It
    // is an expected, handled empty-state signal the admin FE renders
    // inline, not a bug. Kept tight to the bare message so unrelated
    // errors that merely mention the phrase still report.
    if (val === "Feature not configured") return true;
    // Task #5120 — `QuoteApiError: Invalid date format. Use YYYY-MM-DD`
    // (driveshare_v2). The quote client now short-circuits malformed /
    // calendar-impossible dates (stale sessionStorage / URL values) before
    // calling the endpoint and throws this typed 400 itself; on the rare
    // path that still reaches the server it returns the same handled 400.
    // Either way it is an expected client-validation rejection, not a bug.
    // Kept tight to the typed date shape so genuine quote failures (5xx, the
    // handled 422 `QUOTE_UNAVAILABLE`) still report.
    if (typ === "QuoteApiError" && /Invalid date format/i.test(val)) return true;
    // Task #6862 — Web3 wallet extension noise. Browser extensions such as
    // MetaMask inject themselves into every page and emit unhandled promise
    // rejections ("The provider is disconnected from all chains") when the
    // extension loses its RPC connection. Zero real users are affected — this
    // is entirely third-party extension noise from code we never loaded.
    // Kept tight to the exact phrase so no first-party connectivity error is
    // accidentally suppressed.
    if (val.includes("provider is disconnected from all chains")) return true;
    // 2026-08-18 — spell-check/dictation browser-extension noise (Sentry
    // issue cluster V2-F0..FR): a Microsoft-Editor-style extension's
    // internal RPC times out and leaks non-Error unhandled rejections like
    // `Request timeout en_wordsDistributor.getValue`, `Request timeout
    // isDictateAvailable`, `Request timeout getDictionariesByLanguageId`.
    // None of these symbols exist in our code — pure third-party injection
    // noise (25 issues from a single visitor pageview). Kept tight to the
    // extension's exact RPC-name vocabulary so a genuine first-party
    // "Request timeout" error still reports.
    if (
      /Request timeout (?:[\w-]+Distributor\.getValue|is[A-Z]\w*Available|getDictionariesByLanguageId|DefineExpirationForLanguagePacks(?:\.getValue)?)\s*$/.test(
        val,
      )
    )
      return true;
    // Task #6987 — `NotReadableError: The I/O read operation failed` (Sentry
    // event 52aa5de3bd904c27b234d68aa6c266ed, Safari 16.x). This DOMException
    // fires inside posthog-js@1.369.x's WHATWG Streams / ReadableStream
    // controller path when Safari 16 can't read the gzip-compressed capture-
    // endpoint response. PostHog doesn't catch it internally in that version,
    // so it surfaces as an `unhandledrejection`. Upgrading posthog-js removes
    // the streaming path, but we add this belt-and-braces filter so any
    // residual Safari WebKit quirk with the same DOMException shape — where
    // every stack frame is inside posthog-js or another third-party file —
    // does not page us. Kept tight: only drops when NO application frame is
    // present (any first-party `/src/` frame still reports).
    if (typ === "NotReadableError") {
      const frames = v.stacktrace?.frames || [];
      const isThirdPartyFrame = (f: { filename?: string; abs_path?: string }) => {
        const path = f.abs_path || f.filename || "";
        return (
          path === "" ||
          path === "<anonymous>" ||
          /posthog-js/i.test(path) ||
          /node_modules/i.test(path)
        );
      };
      if (frames.length > 0 && frames.every(isThirdPartyFrame)) return true;
    }
    return false;
  });
  return Boolean(dominated);
}

/**
 * Returns `true` when the thrown value is a transient network-level error that
 * the user has no actionable way to resolve (the request never reached our
 * server). These errors are already suppressed from Sentry (see
 * `shouldDropSentryEvent`); this helper makes the same recognition available
 * to catch blocks that want to swap the raw error message for a friendlier
 * "connection problem" description.
 *
 * Recognised patterns (kept in lockstep with `shouldDropSentryEvent`):
 *   • Safari iOS: `TypeError: Load failed`
 *   • Chrome/Firefox: `TypeError: Failed to fetch`
 *   • Firefox extended: `TypeError: NetworkError when attempting to fetch resource`
 *   • Safari iOS with host: `TypeError: Load failed (host)` / `Failed to fetch (host)`
 */
export function isTransientNetworkError(err: unknown): boolean {
  if (!(err instanceof TypeError)) return false;
  const val = err.message;
  if (val === "Failed to fetch") return true;
  if (val === "Load failed") return true;
  if (val === "NetworkError when attempting to fetch resource") return true;
  if (/^Load failed \(.+\)$/.test(val)) return true;
  if (/^Failed to fetch \(.+\)$/.test(val)) return true;
  return false;
}

/**
 * Task #5995 — make an opaque non-Error promise rejection actionable.
 *
 * When code rejects/throws a NON-Error value (e.g. a plain
 * `{ message, name, stack }` object — the shape produced when an Error is
 * structured-cloned across a worker / native bridge, serialized, or hand-rolled)
 * Sentry's `eventFromUnknownInput` cannot read a stack off it and synthesises
 * the placeholder exception value "…captured as promise rejection with keys:
 * message, name, stack" — recording only the KEY NAMES and discarding the actual
 * message and stack (Sentry DRIVESHARE_V2-AX). The issue is then un-triageable.
 *
 * `hint.originalException` still holds the real rejected object, so — for the
 * keyed-rejection fingerprint, and only when the reason carries a non-empty
 * `message`/`stack` — we splice the real message into the exception value (so
 * the issue title is legible) and attach the message/stack to `event.extra`.
 *
 * The content-LESS noise (`[object has no keys]`, forged `CustomEvent`
 * unhandledrejection) is dropped upstream by `shouldDropSentryEvent` and never
 * reaches here, so only content-bearing rejections are enriched. Real `Error`
 * rejections already carry a proper message/stack and don't match the keyed
 * fingerprint, so they are left untouched.
 *
 * Pure and event-mutating; exported so it is unit-tested.
 */
export function enrichKeyedPromiseRejection<T extends SentryEventLike>(
  event: T,
  reason: unknown,
): T {
  const values = event.exception?.values;
  if (!values || values.length === 0) return event;
  if (!reason || typeof reason !== "object") return event;
  const r = reason as { message?: unknown; name?: unknown; stack?: unknown };
  const reasonMessage = typeof r.message === "string" ? r.message.trim() : "";
  const reasonStack = typeof r.stack === "string" ? r.stack.trim() : "";
  const reasonName = typeof r.name === "string" ? r.name.trim() : "";
  if (!reasonMessage && !reasonStack) return event;
  for (const v of values) {
    const val = v.value || "";
    if (!/captured as promise rejection with keys:/i.test(val)) continue;
    if (/\[object has no keys\]/i.test(val)) continue;
    if (reasonMessage) {
      // Make the issue TITLE legible: keep the synthetic marker (so the origin
      // is still obvious) and append the recovered message.
      v.value = `${val} — ${reasonMessage}`;
    }
    const extra: Record<string, unknown> = { ...(event.extra || {}) };
    if (reasonMessage) extra.rejection_message = reasonMessage;
    if (reasonName) extra.rejection_name = reasonName;
    if (reasonStack) extra.rejection_stack = reasonStack;
    event.extra = extra;
  }
  return event;
}

if (dsn && !isTestEnv) {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // Task #3997 — report the SAME release id the source maps were uploaded
    // under (derived once in vite.config.ts, baked here via VITE_SENTRY_RELEASE)
    // so production stack traces de-minify to real component frames instead of
    // bottoming out at React's internal `lazyInitializer`. Undefined in dev /
    // when no release pipeline ran — Sentry simply omits the release tag.
    release:
      (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) || undefined,
    // Session Replay — records a privacy-masked playback of the user
    // session attached to errors so we can see what led up to a crash.
    // Sentry's replay integration masks all text and blocks media by
    // default, so no PII is captured in the recording. We sample lightly
    // for general sessions in production (10%) but capture every session
    // that hits an error (100%) so error replays are never missed. In
    // dev/preview we record every session to make replays easy to verify.
    integrations: [
      Sentry.replayIntegration(),
      // Task #5064 — exclude third-party CDN requests (Apple MapKit, Stripe,
      // PostHog proxy) from automatic `http.client` span creation so the N+1
      // API Call detector has no identical-pattern grid to flag.
      Sentry.browserTracingIntegration({ shouldCreateSpanForRequest }),
    ],
    replaysSessionSampleRate: isProd ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    // Performance monitoring — capture transactions (page loads, navigations,
    // fetch/XHR spans) so slow routes and API calls surface in Sentry's
    // Performance dashboard. Sample lightly in production (10%) to control
    // volume/cost; record every transaction in dev/preview so traces are easy
    // to verify. `browserTracingIntegration` above wires up the automatic
    // instrumentation; this rate is what actually turns tracing on.
    tracesSampleRate: isProd ? 0.1 : 1.0,
    // Only attach tracing headers (sentry-trace / baggage) to same-origin
    // requests so we get connected front-end → API traces without leaking
    // trace ids to third-party hosts (Stripe, MapKit, PostHog proxy). All of
    // our backend calls are same-origin relative paths (`/api/...` via the
    // proxy), so a leading-slash match covers them; we deliberately omit a
    // broad `/api/` pattern that would also match absolute third-party URLs.
    tracePropagationTargets: [/^\//],
    beforeSend(event, hint) {
      // All filtering lives in the extracted pure predicate (Task #4696) so it
      // can be unit-tested. `null` drops the event; otherwise it reports.
      if (shouldDropSentryEvent(event, isProd)) return null;
      // Task #5995 — the surviving event may be an opaque non-Error promise
      // rejection ("captured as promise rejection with keys: …"). Recover the
      // real message/stack from `hint.originalException` so the issue is
      // triageable. No-op for every other event shape.
      return enrichKeyedPromiseRejection(event, hint?.originalException);
    },
    // Task #5240 — strip third-party deny-host spans (Apple MapKit tiles,
    // Stripe, PostHog proxy) from transactions before they are sent so the
    // server-side "Large HTTP payload" / "N+1 API Calls" detectors have nothing
    // to group (Sentry DRIVESHARE_V2-8W / -93). This complements
    // `shouldCreateSpanForRequest`, which only covers fetch/XHR spans and misses
    // the resource-timing tile spans the browser backfills. See
    // `filterTransactionSpans`.
    beforeSendTransaction(event) {
      return filterTransactionSpans(event);
    },
  });
}

export { Sentry };
